import { Button, makeStyles, mergeClasses, shorthands, Spinner, tokens, useAnnounce } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { DismissRegular } from '@fluentui/react-icons';
import { Inject, MultiSelectComponent, SelectEventArgs, VirtualScroll } from '@syncfusion/ej2-react-dropdowns';
import { customPalette } from '../../theme';

import { DataManager, Predicate, Query } from '@syncfusion/ej2-data';
import { EventArgs, SidebarComponent } from '@syncfusion/ej2-react-navigations';
import { debounce, intersection, sortBy } from 'lodash';
import React, { KeyboardEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCalendarPeriods, useGetCases } from '../../api/context/context';
import { useIsFeatureEnabled } from '../../api/features/features';
import { useGetCalculations, useGetEntitiesAndEntityGroups, useSaveTaxReturns } from '../../api/report/report';
import { appInsights } from '../../ApplicationInsightsService';
import { IGroupedTaxReturnsToCreate } from '../../data-types/IGroupedTaxReturnsToCreate';
import { ITaxReturnToCreate } from '../../data-types/ITaxReturnToCreate';
import { useReturnDetails } from '../../hooks/returnDetails/useReturnDetails';
import { useFeedbackMessages } from '../../hooks/useFeedbackMessages';
import {
    Calculation,
    EntityGroupRecord,
    TaxReturnEntity,
    TaxReturnItemRecord,
    TaxReturnSaveRecordsRequest,
    TaxReturnSaveRecordsResponse,
} from '../../model';
import {
    unselectAllElementsFromMultiselect,
    unselectCalculationIfNotPresent,
    unselectStringElementFromMultiselect,
} from '../../utils/AddReturnUtils';
import ConditionalVisibility from '../common/ConditionalVisibility';
import { DefaultTooltipHostV9 } from '../common/DefaultTooltipHostV9';
import IconTabButton from '../common/IconTabButton';
import WarningLabel from '../common/WarningLabel';
import EditReturnModal from '../edit-return/EditReturnModal';
import AddReturnCardGroup from './AddReturnCardGroup';
import AddReturnUnsavedChangesModal from './AddReturnUnsavedChangesModal';
import PanelFocusTrap from './PanelFocusTrap';

const useStyles = makeStyles({
    sidebarContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '-webkit-fill-available',
        backgroundColor: tokens.colorNeutralBackground1,
        padding: '28px 24px 20px 24px',
    },
    sidebarCloseButton: {
        border: 'none',
        color: customPalette.greenDark,
        cursor: 'pointer',
        ':focus': {
            outline: '-webkit-focus-ring-color auto 1px !important',
        },
    },
    sidebarHeaderContent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    sidebarTitle: {
        fontSize: '20px',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '28px',
        margin: '0 0 8px 0',
    },
    sidebarSubTitle: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '20px',
        margin: '0 0 8px 0',
    },
    sidebarFooterContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        justifyContent: 'flex-end',
        padding: '10px 0',
        marginTop: 'auto',
    },
    sidebarFormItem: {
        '& .e-multi-select-wrapper': {
            maxHeight: '150px',
            overflow: 'auto',
        },
    },
    itemsTemplateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    itemsHeaderTemplateContainer: {
        padding: '7px 0px',
    },
    itemTemplate: {
        paddingRight: '2px',
        flex: 1,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
    },
    itemTemplateStretch: {
        flexGrow: 2,
    },
    itemHeaderTemplate: {
        padding: '0 10px',
        fontSize: tokens.fontSizeBase200,
        lineHeight: '16px',
        fontWeight: tokens.fontWeightSemibold,
    },
    nameColumn: {
        maxWidth: '5rem',
    },
    jurisdictionColumn: {
        maxWidth: '5.2rem',
    },
    scrollableSection: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '10px',
        overflow: 'auto',
    },
    warningText: {
        fontWeight: tokens.fontWeightSemibold,
        color: `${tokens.colorStatusWarningForeground2} !important`,
        ':focus': {
            ...shorthands.border('2px', 'solid', 'black'),
            borderRadius: '5px',
        },
    },
});

export interface IAddReturnPanelProps {
    isPanelOpen: boolean;
    handleClose: () => void;
    onSuccess?: (resultKeys: number[]) => void;
    allReturnsNames?: string[];
}

export interface ShowableTaxReturnItemRecord extends TaxReturnItemRecord {
    jurisdictionCode?: string | null | undefined;
    taxYearName?: string | null | undefined;
    entityCode?: string | null | undefined;
    entityLabel?: string | null | undefined;
    calculationName?: string | null | undefined;
    calculationDescription?: string | null | undefined;
    caseCode?: string | null | undefined;
    updateColumnFileKey?: number | null | undefined;
    defaultRangeName?: string | null | undefined;
}

interface IEntitiesOrEntityGroupsToCreate {
    entities: string[];
    entityGroups: string[];
}

interface IEntityOrEntityGroupsTabs {
    isEntitiesTabSelected: boolean;
    selectedEntitiesOrEntityGroup: IEntitiesOrEntityGroupsToCreate;
    setIsEntitiesTabSelected: () => void;
    clearIsEntitiesTabSelected: () => void;
}

const EntityOrEntityGroupsTabs: React.FC<IEntityOrEntityGroupsTabs> = ({
    isEntitiesTabSelected,
    selectedEntitiesOrEntityGroup,
    setIsEntitiesTabSelected,
    clearIsEntitiesTabSelected,
}) => {
    const { t } = useTranslation();

    return (
        <span style={{ display: 'flex', marginBottom: '10px' }}>
            <IconTabButton
                buttonKey={'entityTab'}
                label={t('entity')}
                isActive={isEntitiesTabSelected}
                disabled={selectedEntitiesOrEntityGroup.entityGroups.length > 0}
                onButtonClick={setIsEntitiesTabSelected}
                role='button'
            ></IconTabButton>
            <IconTabButton
                buttonKey={'entityGroupsTab'}
                label={t('entityGroups')}
                isActive={!isEntitiesTabSelected}
                disabled={selectedEntitiesOrEntityGroup.entities.length > 0}
                onButtonClick={clearIsEntitiesTabSelected}
                role='button'
            ></IconTabButton>
        </span>
    );
};

const AddReturnPanel: React.FC<IAddReturnPanelProps> = ({ isPanelOpen, handleClose, onSuccess, allReturnsNames }) => {
    const { t } = useTranslation();
    const { mutateAsync: saveTaxReturns, isLoading: isLoadingSaveTaxReturn } = useSaveTaxReturns();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { data: calculationData } = useGetCalculations();
    const { data: entityOrEntityGroupData } = useGetEntitiesAndEntityGroups();
    const [calculationNames, setCalculationNames] = React.useState<string[]>([]);
    const [selectedEntitiesOrEntityGroup, setSelectedEntitiesOrEntityGroups] = React.useState<IEntitiesOrEntityGroupsToCreate>({
        entities: [],
        entityGroups: [],
    });
    const [groupedTaxReturnsToCreate, setGroupedTaxReturnsToCreate] = React.useState<IGroupedTaxReturnsToCreate[]>([]);

    const hasCards = groupedTaxReturnsToCreate.length > 0 && groupedTaxReturnsToCreate.some((item) => item.taxReturnsToCreate.length > 0);
    const [isEntitiesTabSelected, { setTrue: setIsEntitiesTabSelected, setFalse: clearIsEntitiesTabSelected }] = useBoolean(true);
    const [isEditReturnModalOpen, { setTrue: openEditReturnModal, setFalse: closeEditReturnModal }] = useBoolean(false);
    const [isUnsavedChangesModalOpen, { setTrue: openUnsavedChangesModal, setFalse: closeUnsavedChangesModal }] = useBoolean(false);
    const [editingReturn, setEditingReturn] = useState<ShowableTaxReturnItemRecord | undefined>(undefined);
    const { data: cases } = useGetCases();
    const { data: taxYears } = useGetCalendarPeriods();

    const defaultCase = cases && cases.length > 0 ? cases[0] : undefined;

    const multiSelectCalculation = React.useRef<MultiSelectComponent>(null);
    const multiSelectEntity = React.useRef<MultiSelectComponent>(null);
    const multiSelectEntityGroup = React.useRef<MultiSelectComponent>(null);
    const entityFields = { text: 'entityName', value: 'entityId' };
    const entityGroupsFields = { text: 'groupName', value: 'groupId' };
    // Memoized so the dropdowns' dataSource identity only changes when the data does; a new DataManager per render makes
    // Syncfusion rebind the list and discard the active filter whenever any of the panel's queries resolve.
    const calculationsDataManager = useMemo(() => new DataManager(calculationData), [calculationData]);
    const entityDataManager = useMemo(() => new DataManager(entityOrEntityGroupData?.entities!), [entityOrEntityGroupData?.entities]);
    const entityGroupDataManager = useMemo(
        () => new DataManager(entityOrEntityGroupData?.entityGroups!),
        [entityOrEntityGroupData?.entityGroups]
    );

    if (entityOrEntityGroupData !== undefined) {
        const { entities, entityGroups } = entityOrEntityGroupData;
        const emptyEntities = !entities || entities.length === 0;
        const emptyEntityGroups = !entityGroups || entityGroups.length === 0;
        if (emptyEntities) {
            const message = 'GetEntitiesAndEntityGroups returned empty or null entities list. Entities dropdown will have no options.';
            appInsights?.trackException({
                exception: new Error(`[AddReturnPanel] ${message}`),
                properties: { source: 'AddReturnPanel', listType: 'entities' },
            });
        }
        if (emptyEntityGroups) {
            const message =
                'GetEntitiesAndEntityGroups returned empty or null entity groups list. Entity groups dropdown will have no options.';
            appInsights?.trackException({
                exception: new Error(`[AddReturnPanel] ${message}`),
                properties: { source: 'AddReturnPanel', listType: 'entityGroups' },
            });
        }
    }

    const calculationsLookup: Record<string, Calculation> | undefined = useMemo(() => {
        if (!calculationData) return undefined;

        return calculationData.reduce((acc: Record<string, Calculation>, calculation: Calculation) => {
            acc[calculation.name ?? ''] = calculation;
            return acc;
        }, {});
    }, [calculationData]);

    const entitiesLookup = useMemo(() => {
        if (!entityOrEntityGroupData) return undefined;

        return entityOrEntityGroupData.entities?.reduce((acc: Record<string, TaxReturnEntity>, entity: TaxReturnEntity) => {
            acc[entity.entityId ?? ''] = entity;
            return acc;
        }, {});
    }, [entityOrEntityGroupData]);

    const entityGroupsLookup = useMemo(() => {
        if (!entityOrEntityGroupData) return undefined;

        return entityOrEntityGroupData.entityGroups?.reduce((acc: Record<string, EntityGroupRecord>, entityGroup: EntityGroupRecord) => {
            acc[entityGroup.groupId ?? ''] = entityGroup;
            return acc;
        }, {});
    }, [entityOrEntityGroupData]);

    const { announce } = useAnnounce();

    const { data: isEditCalculationEnhancementsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'EditCalculationEnhancementsEnabled',
    });
    const { data: isAutoPostAmountsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'AutoPostAmountsEnabled',
    });

    const { customRanges, customGroups } = useReturnDetails();

    const addedReturnsNames = useMemo(
        () =>
            groupedTaxReturnsToCreate?.flatMap((taxReturnGroup) =>
                taxReturnGroup.taxReturnsToCreate.map((taxReturnToCreate) => taxReturnToCreate.taxReturnItemName)
            ),
        [groupedTaxReturnsToCreate]
    );

    const duplicatedReturns = useMemo(() => {
        if (isEditCalculationEnhancementsEnabled) {
            return intersection(allReturnsNames, addedReturnsNames) as string[];
        }

        return [];
    }, [allReturnsNames, addedReturnsNames]);

    const amountOfDuplicatedReturns = duplicatedReturns.length;
    const shouldShowDuplicatedReturnsWarning = isEditCalculationEnhancementsEnabled && amountOfDuplicatedReturns >= 1;

    const getRequestData = (): TaxReturnSaveRecordsRequest => {
        const taxReturnItemRecords: TaxReturnItemRecord[] = [];
        groupedTaxReturnsToCreate.forEach((groupedTaxReturn) => {
            groupedTaxReturn.taxReturnsToCreate.forEach((taxReturnToCreate) => {
                taxReturnItemRecords.push({
                    taxReturnItemName: taxReturnToCreate.taxReturnItemName,
                    taxReturnItemDescription: taxReturnToCreate.taxReturnItemDescription,
                    entityKey: groupedTaxReturn.entityKey,
                    calculationKey: taxReturnToCreate.calculation.calculationKey,
                    taxReturnItemCaseKey: taxReturnToCreate.caseKey,
                    taxReturnItemPeriodKey: taxReturnToCreate.periodKey,
                    jurisdictionKey: taxReturnToCreate.calculation.jurisdictionKey,
                    columnFileKey: taxReturnToCreate.columnFileKey,
                    rangeFileKey: taxReturnToCreate.rangeFileKey,
                    includeAllBU: taxReturnToCreate.includeAllBU,
                    ignoreEntityGroup: taxReturnToCreate.ignoreEntityGroup,
                    updateColumnFileKey: taxReturnToCreate.updateColumnFileKey,
                    updateBack: taxReturnToCreate.updateBack,
                    updateBackReport: taxReturnToCreate.updateBackReport,
                    alwaysRefresh: taxReturnToCreate.alwaysRefresh,
                });
            });
        });

        return {
            taxReturnItemRecords: taxReturnItemRecords,
        };
    };

    const onSave = () => {
        saveTaxReturns({ data: getRequestData() }).then((response: TaxReturnSaveRecordsResponse) => {
            if (!response.hasErrors) {
                displayFeedbackMessage({ type: 'success', message: t('calcAdded') });

                if (onSuccess && response.mergeResultCollection) {
                    onSuccess(response.mergeResultCollection.map((result) => result.key ?? 0));
                }
            }
            if (response.hasErrors) {
                displayFeedbackMessage({ type: 'error', message: t('calcNotAdded') });
            }
        });
    };

    const tryCloseOrShowWarning = (): boolean => {
        if (isEditReturnModalOpen) {
            closeEditReturnModal();
            return false;
        }

        // Unsaved-changes dialog is portaled outside the Syncfusion sidebar. While it is open,
        // document clicks (including dialog actions) must not dismiss the panel; dialog buttons
        // own discard vs stay.
        if (isUnsavedChangesModalOpen) {
            return false;
        }

        if (isEditCalculationEnhancementsEnabled && hasCards && !isEditReturnModalOpen) {
            openUnsavedChangesModal();
            return false;
        }

        handleCleanAndClose();
        return true;
    };

    const handleCleanAndClose = () => {
        clearSelectionEntityGroup();
        clearSelectionCalculation();
        clearSelectionEntities();
        setGroupedTaxReturnsToCreate([]);
        setIsEntitiesTabSelected();

        handleClose();
    };

    const styles = useStyles();

    const headerTemplate = () => (
        <span className={mergeClasses(styles.itemsHeaderTemplateContainer, styles.itemsTemplateContainer)}>
            <span className={mergeClasses(styles.itemTemplate, styles.nameColumn, styles.itemHeaderTemplate)}>{t('name')}</span>
            <span className={mergeClasses(styles.itemTemplate, styles.itemTemplateStretch, styles.itemHeaderTemplate)}>
                {t('description')}
            </span>
            <span className={mergeClasses(styles.itemTemplate, styles.itemHeaderTemplate)}>{t('filingType')}</span>
            <span className={mergeClasses(styles.itemTemplate, styles.jurisdictionColumn, styles.itemHeaderTemplate)}>
                {t('jurisdiction')}
            </span>
            <span style={{ width: '26px' }}></span>
        </span>
    );

    const itemTemplate = (item: Calculation) => {
        return (
            <span className={mergeClasses('multiselect-template', styles.itemsTemplateContainer)}>
                <span className={mergeClasses(styles.itemTemplate, styles.nameColumn)}>
                    <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={item.name!}>
                        {item.name}
                    </DefaultTooltipHostV9>
                </span>
                <span className={mergeClasses(styles.itemTemplate, styles.itemTemplateStretch)}>
                    <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={item.description!}>
                        {item.description}
                    </DefaultTooltipHostV9>
                </span>
                <span className={styles.itemTemplate}>
                    <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={item.filingTypeDescription!}>
                        {item.filingTypeDescription}
                    </DefaultTooltipHostV9>
                </span>
                <span className={mergeClasses(styles.itemTemplate, styles.jurisdictionColumn)}>
                    <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={item.jurisdictionCode!}>
                        {item.jurisdictionCode}
                    </DefaultTooltipHostV9>
                </span>
            </span>
        );
    };

    const itemTemplateEntity = useCallback((item: any) => {
        const name = item.entityName ?? item.groupDescription;
        const code = item.entityCode ?? item.groupName;
        return `${code} - ${name}`;
    }, []);

    const destroyFocusTrapZoneOnEscape: KeyboardEventHandler<HTMLDivElement> = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            tryCloseOrShowWarning();
        }
    };

    const calculationFields = { text: 'description', value: 'name' };

    // Handlers are memoized so a re-render doesn't replace them (and orphan a pending debounced call) mid-typing.
    const onFiltering = useMemo(
        () =>
            debounce((e: any) => {
                let query: Query = new Query();
                const predicates: Predicate[] = [
                    new Predicate('name', 'contains', e.text, true),
                    new Predicate('description', 'contains', e.text, true),
                    new Predicate('jurisdictionCode', 'contains', e.text, true),
                    new Predicate('filingTypeDescription', 'contains', e.text, true),
                ];

                query = e.text !== '' ? query.where(Predicate.or(predicates)) : query;
                e.updateData(calculationsDataManager, query);
            }, 400),
        [calculationsDataManager]
    );

    const onFilteringEntity = useMemo(
        () =>
            debounce((e: any) => {
                let query = new Query();
                const predicates: Predicate[] = [new Predicate('displayName', 'contains', e.text, true)];

                query = e.text !== '' ? query.where(Predicate.or(predicates)) : query;
                e.updateData(entityDataManager, query);
            }, 400),
        [entityDataManager]
    );

    const onFilteringEntityGroup = useMemo(
        () =>
            debounce((e: any) => {
                let query = new Query();
                const predicates: Predicate[] = [new Predicate('displayName', 'contains', e.text, true)];

                query = e.text !== '' ? query.where(Predicate.or(predicates)) : query;
                e.updateData(entityGroupDataManager, query);
            }, 400),
        [entityGroupDataManager]
    );

    const createTaxReturnToCreate = (
        calculationName: string,
        entityCode: string,
        isEntityGroup: boolean,
        entityRangeName?: string | null,
        entity?: TaxReturnEntity
    ): ITaxReturnToCreate | undefined => {
        const calculation = calculationsLookup?.[calculationName];

        if (calculation) {
            const defaultTaxYear = taxYears?.find((taxYear) => taxYear.periodYear === calculation.taxYear);
            const rangeNameForEntity = entity?.isSCorp || entity?.isPartnership ? undefined : entityRangeName;
            const defaultRangeFileKey = customRanges?.find((range) => range.name === rangeNameForEntity)?.rangeKey;

            let defaultColumnFileKey: number | null | undefined;
            let defaultIgnoreEntityGroup: boolean | undefined;
            if ((entity?.isPartnership || entity?.isSCorp) && entity?.partnerColumnFile && customGroups?.length) {
                const matchingCustomGroup = customGroups.find(
                    (cg) => cg.nameAndDescription === entity.partnerColumnFile || cg.name === entity.partnerColumnFile
                );
                if (matchingCustomGroup?.customGroupKey != null) {
                    defaultColumnFileKey = matchingCustomGroup.customGroupKey;
                    defaultIgnoreEntityGroup = true;
                }
            }

            const isSCorpOrPartnership = entity?.isSCorp || entity?.isPartnership;
            const taxReturnToCreate: ITaxReturnToCreate = {
                calculation: calculation,
                jurisdictionCode: calculation.jurisdictionCode!,
                caseKey: defaultCase?.key ?? 0,
                caseCode: defaultCase?.code ?? '',
                periodKey: defaultTaxYear?.key ?? 0,
                taxYearName: defaultTaxYear?.name ?? '',
                taxReturnItemName: `${entityCode} ${calculation.name} ${calculation.description} ${t('case').toString()} ${
                    defaultCase?.code
                }, ${defaultTaxYear?.name}`,
                taxReturnItemDescription: `${calculation.description}`,
                alwaysRefresh: true,
                includeAllBU: !isEntityGroup && !isSCorpOrPartnership,
                rangeFileKey: defaultRangeFileKey,
                ...(defaultColumnFileKey != null && { columnFileKey: defaultColumnFileKey }),
                ...(defaultIgnoreEntityGroup !== undefined && { ignoreEntityGroup: defaultIgnoreEntityGroup }),
            };
            if (isAutoPostAmountsEnabled) {
                return {
                    ...taxReturnToCreate,
                    updateBack: true,
                    updateBackReport: true,
                };
            }
            return taxReturnToCreate;
        }

        return undefined;
    };

    const addCalculationCardToAllEntities = (
        currentState: IGroupedTaxReturnsToCreate[],
        newCalculationName: string
    ): IGroupedTaxReturnsToCreate[] => {
        currentState.forEach((item) => {
            const newArray = [...item.taxReturnsToCreate];
            const entityForPartnership = !item.isEntityGroup ? entitiesLookup?.[item.entityId] : undefined;
            const rangeNameForItem = entityForPartnership?.isSCorp || entityForPartnership?.isPartnership ? undefined : item.rangeName;
            const newTaxReturnToCreate = createTaxReturnToCreate(
                newCalculationName,
                item.entityCode,
                item.isEntityGroup,
                rangeNameForItem ?? undefined,
                entityForPartnership
            );

            if (newTaxReturnToCreate) {
                newArray.push(newTaxReturnToCreate);
            }

            item.taxReturnsToCreate = sortBy(newArray, (item) => item.taxReturnItemName);
        });

        return currentState;
    };

    const removeCalculationCardFromAllEntities = (
        currentState: IGroupedTaxReturnsToCreate[],
        removedCalculationName: string
    ): IGroupedTaxReturnsToCreate[] => {
        const entityIdsToRemove: string[] = [];
        const existingCalculations = getAllCalculationsInUse();

        currentState.forEach((item) => {
            item.taxReturnsToCreate = item.taxReturnsToCreate.filter(
                (taxReturnToCreate) => taxReturnToCreate.calculation.name !== removedCalculationName
            );

            if (existingCalculations.length > 1 && item.taxReturnsToCreate.length === 0) {
                if (isEntitiesTabSelected) {
                    updateEntitySelection(item.entityId);
                } else {
                    clearSelectionEntityGroup();
                }
                entityIdsToRemove.push(item.entityId);
            }
        });

        currentState = currentState.filter((item) => !entityIdsToRemove.includes(item.entityId));

        return currentState;
    };

    const getSelectedCalculationsByName = (selectedCalculationNames: string[]) => {
        if (calculationsLookup) {
            return selectedCalculationNames.map((calculationName) => calculationsLookup[calculationName]);
        }

        return [];
    };

    const updateCalculations = (selectedCalculationNames: string[]) => {
        const selectedCalculations = getSelectedCalculationsByName(selectedCalculationNames);

        let stateCopy = [...groupedTaxReturnsToCreate];
        const calculationsToRemove = calculationNames.filter((calculation) => !selectedCalculationNames.includes(calculation));
        const calculationsToAdd = selectedCalculations.filter((calculation) => !calculationNames.includes(calculation.name ?? ''));

        calculationsToRemove.forEach((removedCalculationName) => {
            stateCopy = removeCalculationCardFromAllEntities(stateCopy, removedCalculationName);
        });

        calculationsToAdd.forEach((newCalculation) => {
            stateCopy = addCalculationCardToAllEntities(stateCopy, newCalculation.name ?? '');
        });

        setGroupedTaxReturnsToCreate(stateCopy);
        setCalculationNames(selectedCalculationNames);
    };

    const getAllCalculationsInUse = (discardedEntity?: TaxReturnEntity) => {
        const allCalculationsInCards: string[] = groupedTaxReturnsToCreate
            .filter((groupedTaxReturn) => discardedEntity === undefined || groupedTaxReturn.entityName !== discardedEntity.entityName)
            .flatMap((groupedTaxReturn) => groupedTaxReturn.taxReturnsToCreate.map((taxReturn) => taxReturn.calculation.name!));

        return allCalculationsInCards;
    };

    const removeCalculationChipIfNecessary = (args: SelectEventArgs) => {
        if (selectedEntitiesOrEntityGroup.entities.length > 1) {
            const discardedEntity = args.itemData as TaxReturnEntity;
            const calculationsInUseForOtherEntities: string[] = getAllCalculationsInUse(discardedEntity);

            const currentlySelectedCalculations = (multiSelectCalculation.current?.value as Calculation[]) ?? [];

            currentlySelectedCalculations.forEach((selectedCalculation) => {
                if (!calculationsInUseForOtherEntities.includes(selectedCalculation.name!)) {
                    updateCalculationSelection(selectedCalculation.name!);
                }
            });
        }
    };

    const getSelectedEntitiesById = (selectedEntityNames: string[]): TaxReturnEntity[] => {
        if (entitiesLookup) {
            return selectedEntityNames.map((entityId) => entitiesLookup[entityId]);
        }

        return [];
    };

    const updateEntities = (selectedEntityNames: string[]) => {
        const selectedEntities = getSelectedEntitiesById(selectedEntityNames);

        let stateCopy = [...groupedTaxReturnsToCreate];
        stateCopy = stateCopy.filter((item) => selectedEntities.some((selectedEntity) => selectedEntity.entityId === item.entityId));

        selectedEntities.forEach((entity) => {
            if (!stateCopy.some((item) => item.entityId === entity.entityId)) {
                const rangeNameForEntity = entity.isSCorp || entity.isPartnership ? undefined : entity.rangeName;
                stateCopy.push({
                    entityId: entity.entityId ?? '',
                    entityName: entity.entityName ?? '',
                    entityCode: entity.entityCode ?? '',
                    entityKey: entity.entityKey ?? 0,
                    isEntityGroup: false,
                    rangeName: rangeNameForEntity,
                    taxReturnsToCreate: calculationNames
                        .map((calculation) =>
                            createTaxReturnToCreate(calculation, entity.entityCode!, false, rangeNameForEntity ?? undefined, entity)
                        )
                        .filter((item) => item !== undefined) as ITaxReturnToCreate[],
                });
            }
        });

        setGroupedTaxReturnsToCreate(sortBy(stateCopy, (item) => item.entityName));
        setSelectedEntitiesOrEntityGroups({
            entities: selectedEntityNames,
            entityGroups: [],
        });
    };

    const getSelectedEntityGroupsById = (selectedEntityGroupNames: string[]): EntityGroupRecord[] => {
        if (entityGroupsLookup) {
            return selectedEntityGroupNames.map((entityGroupId) => entityGroupsLookup[entityGroupId]);
        }

        return [];
    };

    const updateEntityGroups = (selectedEntityGroupIds: string[]) => {
        const selectedEntityGroups = getSelectedEntityGroupsById(selectedEntityGroupIds);
        let stateCopy = [...groupedTaxReturnsToCreate];
        stateCopy = stateCopy.filter((item) =>
            selectedEntityGroups.some((selectedEntityGroup) => selectedEntityGroup.groupId === item.entityId)
        );

        selectedEntityGroups.forEach((entityGroup) => {
            if (!stateCopy.some((item) => item.entityId === entityGroup.groupId)) {
                stateCopy.push({
                    entityId: entityGroup.groupId ?? '',
                    entityName: entityGroup.groupDescription ?? '',
                    entityCode: entityGroup.groupName ?? '',
                    entityKey: entityGroup.groupKey ?? 0,
                    isEntityGroup: true,
                    taxReturnsToCreate: sortBy(
                        calculationNames
                            .map((calculation) => createTaxReturnToCreate(calculation, entityGroup.groupName!, true))
                            .filter((item) => item !== undefined) as ITaxReturnToCreate[],
                        (item) => item.taxReturnItemName
                    ),
                });
            }
        });

        setGroupedTaxReturnsToCreate(sortBy(stateCopy, (item) => item.entityName));
        setSelectedEntitiesOrEntityGroups({
            entities: [],
            entityGroups: selectedEntityGroupIds,
        });
    };

    // Since the multiselect elements are technically outside of the panel element clicking on them will close the panel
    const isClickOnMultiselect = (args: EventArgs) => {
        if (args.event && args.event.target) {
            const targetAsElement = args.event.target as HTMLElement;
            return targetAsElement.closest('.e-multi-select-list-wrapper') || targetAsElement.classList.contains('e-chips-close');
        }

        return false;
    };

    // Fluent v9 Dialog surfaces are portaled to document.body; treat them like multiselect popups
    // so Syncfusion closeOnDocumentClick does not dismiss the panel when the user acts in a dialog.
    const isClickOnPortaledDialog = (args: EventArgs) => {
        if (args.event && args.event.target) {
            const targetAsElement = args.event.target as HTMLElement;
            return Boolean(targetAsElement.closest('.fui-DialogSurface') || targetAsElement.closest('.fui-Dialog'));
        }

        return false;
    };

    const clearGlobalEventListenerAndClose = (args: EventArgs) => {
        if (isClickOnMultiselect(args) || isClickOnPortaledDialog(args) || !tryCloseOrShowWarning()) {
            args.cancel = true;
        }
    };

    const getUpdateTaxReturnToCreateObject = (
        previousValue: ITaxReturnToCreate,
        taxReturn: ShowableTaxReturnItemRecord
    ): ITaxReturnToCreate => {
        return {
            ...previousValue,
            taxReturnItemName: taxReturn.taxReturnItemName ?? '',
            taxReturnItemDescription: taxReturn.taxReturnItemDescription ?? '',
            caseKey: taxReturn.taxReturnItemCaseKey ?? 0,
            caseCode: taxReturn.caseCode ?? '',
            periodKey: taxReturn.taxReturnItemPeriodKey ?? 0,
            taxYearName: taxReturn.taxYearName ?? '',
            columnFileKey: taxReturn.columnFileKey,
            rangeFileKey: taxReturn.rangeFileKey,
            includeAllBU: taxReturn.includeAllBU,
            ignoreEntityGroup: taxReturn.ignoreEntityGroup,
            updateColumnFileKey: taxReturn.updateColumnFileKey,
            updateBack: taxReturn.updateBack,
            updateBackReport: taxReturn.updateBackReport,
        };
    };

    const onSubmitEditReturn = (editedData: ShowableTaxReturnItemRecord | undefined) => {
        if (!editedData) {
            return;
        }

        const stateCopy = [...groupedTaxReturnsToCreate];
        const editedEntityIndex = stateCopy.findIndex((groupedTaxReturn) => groupedTaxReturn.entityKey === editedData.entityKey);

        if (editedEntityIndex !== -1) {
            const returnIndex = stateCopy[editedEntityIndex]?.taxReturnsToCreate.findIndex(
                (returnToCreate) => returnToCreate.calculation.calculationKey === editedData.calculationKey
            );

            if (returnIndex !== -1) {
                stateCopy[editedEntityIndex].taxReturnsToCreate[returnIndex] = getUpdateTaxReturnToCreateObject(
                    stateCopy[editedEntityIndex].taxReturnsToCreate[returnIndex],
                    editedData
                );

                setGroupedTaxReturnsToCreate(stateCopy);
            }
        }
    };

    const onEditReturn = (taxReturn: ShowableTaxReturnItemRecord) => {
        setEditingReturn(taxReturn);
        openEditReturnModal();
    };

    const panelAccessibleName = `${t('addNewCalculation')} panel`;

    useEffect(() => {
        if (!isPanelOpen) return;
        announce(panelAccessibleName, { batchId: 'add-return-panel-open' });
    }, [isPanelOpen, panelAccessibleName, announce]);

    const updateCalculationSelection = (calculationName: string) => {
        unselectStringElementFromMultiselect(multiSelectCalculation.current, (item) => item !== calculationName, setCalculationNames);
    };

    const updateEntitySelection = (entityId: string) => {
        unselectStringElementFromMultiselect(
            multiSelectEntity.current,
            (item) => item !== entityId,
            (newValue) => {
                setSelectedEntitiesOrEntityGroups({
                    entities: newValue,
                    entityGroups: [],
                });
            }
        );
    };

    const clearSelectionEntityGroup = () => {
        unselectAllElementsFromMultiselect(multiSelectEntityGroup.current);
        setSelectedEntitiesOrEntityGroups({
            entities: [],
            entityGroups: [],
        });
    };

    const clearEntityOrEntityGroupSelection = (entityId: string) => {
        if (isEntitiesTabSelected) {
            updateEntitySelection(entityId);
        } else {
            clearSelectionEntityGroup();
        }
    };

    const clearSelectionCalculation = () => {
        unselectAllElementsFromMultiselect(multiSelectCalculation.current);
        setCalculationNames([]);
    };

    const clearSelectionEntities = () => {
        unselectAllElementsFromMultiselect(multiSelectEntity.current);
        setSelectedEntitiesOrEntityGroups({
            entities: [],
            entityGroups: [],
        });
    };

    const getTaxReturnItemName = (entityIndex: number, calculationId: string): string | undefined => {
        return groupedTaxReturnsToCreate[entityIndex].taxReturnsToCreate.find((item) => item.calculation.id === calculationId)
            ?.taxReturnItemName;
    };

    const removeCard = (entityId: string, calculationId: string) => {
        const stateCopy = [...groupedTaxReturnsToCreate];
        const entityIndex = stateCopy.findIndex((item) => item.entityId === entityId);

        if (entityIndex > -1) {
            const entityName = stateCopy[entityIndex].entityName;
            const taxReturnItemName = getTaxReturnItemName(entityIndex, calculationId);

            stateCopy[entityIndex].taxReturnsToCreate = stateCopy[entityIndex].taxReturnsToCreate.filter(
                (item) => item.calculation.id !== calculationId
            );

            if (stateCopy[entityIndex].taxReturnsToCreate.length === 0) {
                clearEntityOrEntityGroupSelection(entityId);

                stateCopy.splice(entityIndex, 1);
                announce(`${entityName} Entity Card deleted`);
            }

            unselectCalculationIfNotPresent(
                calculationId,
                getSelectedCalculationsByName(calculationNames),
                stateCopy,
                updateCalculationSelection
            );

            setGroupedTaxReturnsToCreate(stateCopy);
            announce(`${taxReturnItemName} Calculation Card item deleted`);
        }
    };

    const getDuplicatedReturnsWarningMessages = (): string => {
        if (shouldShowDuplicatedReturnsWarning) {
            return amountOfDuplicatedReturns === 1
                ? t('singleDuplicatedReturn').toString()
                : t('multipleDuplicatedReturns', { count: amountOfDuplicatedReturns }).toString();
        }

        return '';
    };

    return (
        <>
            <PanelFocusTrap
                enabled={isPanelOpen && !isEditReturnModalOpen && !isUnsavedChangesModalOpen}
                onKeyDownCapture={destroyFocusTrapZoneOnEscape}
            >
                <SidebarComponent
                    id='addNewReturnDockSidebar'
                    data-testid='addNewReturnDockSidebar'
                    close={clearGlobalEventListenerAndClose}
                    isOpen={isPanelOpen}
                    position='Right'
                    width={644}
                    type='Over'
                    tabIndex={0}
                    showBackdrop={true}
                    closeOnDocumentClick={isEditReturnModalOpen || isUnsavedChangesModalOpen ? false : true}
                >
                    <div className={styles.sidebarContainer}>
                        <div className={styles.sidebarHeaderContent}>
                            <span className={styles.sidebarTitle}>{t('addNewCalculation')}</span>
                            <Button
                                appearance='subtle'
                                icon={<DismissRegular />}
                                aria-label={t('closePanel').toString()}
                                onClick={tryCloseOrShowWarning}
                                className={styles.sidebarCloseButton}
                            />
                        </div>
                        <div className={styles.scrollableSection}>
                            <div className={styles.sidebarFormItem}>
                                <span className={styles.sidebarSubTitle}>{t('formsOrCalculations')}</span>
                                <MultiSelectComponent
                                    id='multiselect-calculations'
                                    ref={multiSelectCalculation}
                                    value={calculationNames}
                                    onChange={(e: any) => {
                                        // Ignore programmatic value sync from delete/unselect helpers so
                                        // card-list state is not recomputed from a stale React snapshot.
                                        if (e.isInteracted === false) {
                                            return;
                                        }
                                        updateCalculations(e.value ?? []);
                                    }}
                                    changeOnBlur={false}
                                    fields={calculationFields}
                                    dataSource={calculationsDataManager}
                                    placeholder={t('selectFormsAndCalculations').toString()}
                                    headerTemplate={headerTemplate}
                                    itemTemplate={itemTemplate}
                                    filtering={onFiltering.bind(this)}
                                    allowFiltering={true}
                                    enableVirtualization={true}
                                    closePopupOnSelect={false}
                                    hideSelectedItem={false}
                                    showDropDownIcon={true}
                                    mode='Box'
                                >
                                    <Inject services={[VirtualScroll]} />
                                </MultiSelectComponent>
                            </div>
                            <div>
                                <span className={styles.sidebarSubTitle}>{t('entityOrEntityGroups')}</span>
                                <EntityOrEntityGroupsTabs
                                    isEntitiesTabSelected={isEntitiesTabSelected}
                                    selectedEntitiesOrEntityGroup={selectedEntitiesOrEntityGroup}
                                    setIsEntitiesTabSelected={setIsEntitiesTabSelected}
                                    clearIsEntitiesTabSelected={clearIsEntitiesTabSelected}
                                />
                                <ConditionalVisibility isVisible={isEntitiesTabSelected}>
                                    <MultiSelectComponent
                                        id='multiselect-entities'
                                        ref={multiSelectEntity}
                                        value={selectedEntitiesOrEntityGroup.entities}
                                        key={'entityTab'}
                                        onChange={(e: any) => {
                                            if (e.isInteracted === false) {
                                                return;
                                            }
                                            updateEntities(e.value ?? []);
                                        }}
                                        fields={entityFields}
                                        dataSource={entityDataManager}
                                        placeholder={t('selectEntity').toString()}
                                        itemTemplate={itemTemplateEntity}
                                        filtering={onFilteringEntity.bind(this)}
                                        allowFiltering={true}
                                        enableVirtualization={true}
                                        closePopupOnSelect={false}
                                        hideSelectedItem={false}
                                        showDropDownIcon={true}
                                        mode='Box'
                                        changeOnBlur={false}
                                        removed={removeCalculationChipIfNecessary}
                                    >
                                        <Inject services={[VirtualScroll]} />
                                    </MultiSelectComponent>
                                </ConditionalVisibility>
                                <ConditionalVisibility isVisible={!isEntitiesTabSelected}>
                                    <MultiSelectComponent
                                        id='multiselect-entity-groups'
                                        ref={multiSelectEntityGroup}
                                        value={selectedEntitiesOrEntityGroup.entityGroups}
                                        key={'entityGroupsTab'}
                                        onChange={(e: any) => {
                                            if (e.isInteracted === false) {
                                                return;
                                            }
                                            updateEntityGroups(e.value ?? []);
                                        }}
                                        fields={entityGroupsFields}
                                        dataSource={entityGroupDataManager}
                                        placeholder={t('selectEntityGroup').toString()}
                                        itemTemplate={itemTemplateEntity}
                                        filtering={onFilteringEntityGroup.bind(this)}
                                        allowFiltering={true}
                                        enableVirtualization={true}
                                        closePopupOnSelect={true}
                                        hideSelectedItem={false}
                                        showDropDownIcon={true}
                                        maximumSelectionLength={1}
                                        mode='Box'
                                        changeOnBlur={false}
                                    >
                                        <Inject services={[VirtualScroll]} />
                                    </MultiSelectComponent>
                                </ConditionalVisibility>
                            </div>
                            {shouldShowDuplicatedReturnsWarning && (
                                <WarningLabel
                                    warningMessage={getDuplicatedReturnsWarningMessages()}
                                    labelClassName={styles.warningText}
                                    iconTestId='duplicated-returns-in-panel-icon'
                                />
                            )}
                            {groupedTaxReturnsToCreate.map((item: any, index: number) => {
                                return (
                                    <AddReturnCardGroup
                                        index={index}
                                        duplicatedReturns={duplicatedReturns}
                                        key={item.entityName}
                                        groupedTaxReturnToCreate={item}
                                        onRemoveClicked={removeCard}
                                        onEditReturn={onEditReturn}
                                    />
                                );
                            })}
                        </div>
                        <div className={styles.sidebarFooterContainer}>
                            <Button
                                appearance='primary'
                                aria-label={t('add').toString()}
                                onClick={onSave}
                                disabled={Boolean(!hasCards || isLoadingSaveTaxReturn)}
                            >
                                {t('add').toString()}
                                {isLoadingSaveTaxReturn && <Spinner size='extra-tiny' />}
                            </Button>
                            <Button aria-label={t('close').toString()} appearance='secondary' onClick={tryCloseOrShowWarning}>
                                {t('cancel').toString()}
                            </Button>
                        </div>
                    </div>
                </SidebarComponent>
            </PanelFocusTrap>
            <EditReturnModal
                isOpen={isEditReturnModalOpen}
                selectedReturn={editingReturn}
                onClose={closeEditReturnModal}
                onSubmit={onSubmitEditReturn}
                allReturnsNames={allReturnsNames}
            />

            <AddReturnUnsavedChangesModal
                isModalVisible={isUnsavedChangesModalOpen}
                onCancel={closeUnsavedChangesModal}
                onConfirm={() => {
                    handleCleanAndClose();
                    closeUnsavedChangesModal();
                }}
            />
        </>
    );
};

export default AddReturnPanel;
